import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {Observable} from "rxjs";
import {RemarkData} from "../../models/remarkDataModel";
import {
    selectIsLoadingTrafficData,
    selectIsSavingRemark,
    selectRemarkData,
    selectTrafficData,
    selectError
} from "../../store/createoredit/createoredit.selector";
import {
    createRemarkData,
    clearRemarkData,
    updateRemarkData,
    fetchRemarkData,
    fetchCurrentAndNextYearTrafficData,
} from "../../store/createoredit/createoredit.action";
import {Data, GetAllWrappedResponse} from "../../models/getAllWrappedDataModel";
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {
    Select2Data,
    Select2Option,
    Select2SearchEvent,
    Select2UpdateEvent,
    Select2UpdateValue,
    Select2Value
} from "ng-select2-component";
import { AuthService } from "../../service/auth.service";
import { userReadWriteGroups } from "../../../assets/user-groups";
import { CalendarService } from "../../service/calendar.service";
import { CreateOrEditFormGroup } from "../../models/createOrEditFormGroupModel";
import { DeleteModalComponent } from "../deleteModal.component";
import { deleteExceptionData } from "../../store/createorupdateexception/createorupdateexception.action";
import { MatDialog } from "@angular/material/dialog";
import { NoopScrollStrategy } from "@angular/cdk/overlay";

type Month = { header: string, formControl: FormControl<Date[] | null | undefined>, start: Date, end: Date };
type Selected = {
    disabledDates: Date[];
    calendarMonths: Array<Month>;
    name: string
    validTrains: GetAllWrappedResponse["data"][number]["validTrains"]
    selectableTrainNumberOptions: Select2Data;
    selectedTrainIds: Select2Value[],
    trafficData: Data | null
    startDate: Date
    endDate: Date
};

@Component({
    selector: "app-createoredit",
    templateUrl: "./createoredit.component.html",
    styleUrl: "./createoredit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateOrEditComponent implements OnInit, OnDestroy {
    trafficData$ = new Observable<GetAllWrappedResponse["data"]>();
    trafficData!: GetAllWrappedResponse["data"];
    remarkData$ = new Observable<RemarkData | null>();
    remarkData: RemarkData | null = null; // Remark is not multi-year supported, but just connected to id
    remarkId: string = '';
    isEditMode: boolean = false;
    isLoading$: Observable<boolean>;
    isSaving$: Observable<boolean>;
    error$: Observable<any | null>;
    form: CreateOrEditFormGroup = this.generateFormGroup({
        id: '',
        name: '',
        suffixLetter: '',
        active: false,
        dates: [],
        weekdaySelected: false,
        saturdaySelected: false,
        sundaySelected: false,
    });
    selectedYear: Selected = {
        disabledDates: [],
        calendarMonths: [],
        name: "",
        validTrains: [],
        selectableTrainNumberOptions: [],
        selectedTrainIds: [],
        trafficData: null,
        startDate: new Date(),
        endDate: new Date(),
    }

  constructor(
        private authService: AuthService,
        private calendarService: CalendarService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private fb: FormBuilder,
        private dialog: MatDialog,
        private store: Store) {
        this.trafficData$ = this.store.select(selectTrafficData);
        this.remarkData$ = this.store.select(selectRemarkData);
        this.isLoading$ = this.store.select(selectIsLoadingTrafficData);
        this.isSaving$ = this.store.select(selectIsSavingRemark);
        this.error$ = this.store.select(selectError);
    }

    ngOnInit(): void {
        this.authService.hasReadWriteGroups(userReadWriteGroups.DLI);
        this.store.dispatch(fetchCurrentAndNextYearTrafficData());

        this.activatedRoute.paramMap.subscribe(param => {
            this.remarkId = param.get("remarkId")!;
            this.isEditMode = !!this.remarkId;
            if (this.remarkId) {
                this.store.dispatch(fetchRemarkData({ remarkId: this.remarkId }))
            }
        });

        this.trafficData$.subscribe(trafficData => {
            this.trafficData = trafficData;

            if (this.isEditMode) {
                this.remarkData$.subscribe(remark => {
                    this.remarkData = remark;
                    this.form = this.generateFormGroup({
                        ...remark!,
                        dates: [],
                    });

                    if (trafficData[0]) {
                        this.setSelectedYearData(trafficData[0]);
                    }
                })
            } else {
                // is createMode
                this.form = this.generateFormGroup({
                    id: null,
                    name: '',
                    suffixLetter: '',
                    active: false,
                    dates: [],
                    weekdaySelected: false,
                    saturdaySelected: false,
                    sundaySelected: false,
                });

                if (trafficData[0]) {
                    this.setSelectedYearData(trafficData[0]);
                }
            }
        })
    }

    ngOnDestroy(): void {
        // Needed to not flash previous page's selectedTrains
        this.store.dispatch(clearRemarkData());
    }

    generateFormGroup(remark: Pick<RemarkData, "id" | "dates" | "name" | "suffixLetter" | "active" | "weekdaySelected" | "saturdaySelected" | "sundaySelected">): CreateOrEditFormGroup {
        return this.fb.nonNullable.group({
            id: this.fb.nonNullable.control(remark.id),
            dates: this.fb.nonNullable.control(remark.dates || []),
            name: this.fb.nonNullable.control(remark.name, [
                Validators.required,
                Validators.maxLength(50)
            ]),
            suffixLetter: this.fb.nonNullable.control(remark.suffixLetter, [
                Validators.required,
                Validators.maxLength(1)
            ]),
            active: this.fb.nonNullable.control(remark.active),
            weekdaySelected: this.fb.nonNullable.control(remark.weekdaySelected),
            saturdaySelected: this.fb.nonNullable.control(remark.saturdaySelected),
            sundaySelected: this.fb.nonNullable.control(remark.sundaySelected),
        }) as CreateOrEditFormGroup;
    }

    generateTrainNumberSelectData(trainNumberData: Select2Data): Select2Data {
        return [{
            label: '',
            data: [
                {
                    label: 'Välj alla',
                    onClick: this.selectAllTrains.bind(this),
                },
                {
                    label: 'Välj norrgående',
                    onClick: this.selectNorthboundTrains.bind(this),
                },
                {
                    label: 'Välj södergående',
                    onClick: this.selectSouthboundTrains.bind(this),
                }],
            options: trainNumberData as Select2Option[],
        }];
    }

    setSelectedYearData(trafficData: GetAllWrappedResponse["data"][number]) {
        // First clear all changed year-dependent inputs
        this.clearAllTrains();
        this.form.controls.weekdaySelected.reset()
        this.form.controls.saturdaySelected.reset()
        this.form.controls.sundaySelected.reset()

        // Then set all initial values for selected year
        this.selectedYear.trafficData = trafficData;
        this.selectedYear.name = trafficData.name;
        this.selectedYear.validTrains = trafficData.validTrains;
        this.selectedYear.startDate = new Date(trafficData.startDate)
        this.selectedYear.endDate = new Date(trafficData.endDate)
        this.selectedYear.selectableTrainNumberOptions = this.generateTrainNumberSelectData(trafficData.validTrains.map(s => ({
            value: s.trainNumber_FiveDigits,
            label: s.trainNumber_FiveDigits
        })));

        if (this.remarkData?.trainNumbers) {
            // remark is not multi-year compliant, but we want to reset the selected trains when switching years.
            this.selectedYear.selectedTrainIds = this.remarkData.trainNumbers || [];
        }

        const yearDates = this.calendarService.getMonthsBetweenDates(trafficData.startDate, trafficData.endDate);
        const preselectedDates = this.remarkData?.dates.map(date => new Date(date)) || [];
        this.selectedYear.calendarMonths = yearDates.map(monthDate => {
            const selectedDatesInMonth = preselectedDates.filter(a => a.getMonth() === monthDate.getMonth() && a.getFullYear() === monthDate.getFullYear())
            return ({
                header: monthDate.toLocaleString('sv-SE', {month: 'long', year: 'numeric'}),
                formControl: new FormControl(selectedDatesInMonth.length ? selectedDatesInMonth : null),
                start: monthDate,
                end: this.calendarService.getLastDayOfMonth(monthDate),
            });
        });
    }

    goToAdmin() {
        this.router.navigate(["admin"]);
    }

    openDeleteExceptionModal($event: MouseEvent, id: number) {
        const clickedException = this.remarkData?.exceptions.find(exception => exception.id === id);

        this.dialog.open(DeleteModalComponent, {
            width: '400px',
            data: {
                title: 'Ta bort undantag',
                message: `Är du säker på att du vill ta bort undantaget: ${clickedException?.name}?`,
                noLabel: 'Nej',
                yesLabel: 'Ja',
            },
            scrollStrategy: new NoopScrollStrategy(),
        }).afterClosed().subscribe((result: boolean) => {
            if (result) {
                this.store.dispatch(deleteExceptionData({ remarkId: this.remarkId, exceptionId: id }));
            }
        });
    }

    private generatePayload() {
        const selectedDates = this.selectedYear.calendarMonths.flatMap(date => date.formControl.value!).filter(Boolean) || [];
        const remarkPayload: RemarkData = {
            ...this.remarkData!,
            ...this.form.value,
            dates: selectedDates.map(date => this.calendarService.convertToStringDateWithTimezone(date)),
            trainNumbers: this.selectedYear.selectedTrainIds as string[],
        };
        return remarkPayload;
    }

    updateRemark() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        if (this.selectedYear.selectedTrainIds.length === 0) {
            return;
        }
        const remarkPayload = this.generatePayload();
        this.store.dispatch(updateRemarkData({ remark: remarkPayload }))
    }

    createRemark() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        if (this.selectedYear.selectedTrainIds.length === 0) {
            return;
        }
        const remarkPayload = this.generatePayload();
        this.store.dispatch(createRemarkData({ remark: remarkPayload }))
    }

    selectAllTrains() {
        this.selectedYear.selectedTrainIds = this.selectedYear.validTrains!.map(a => a.trainNumber_FiveDigits);
    }

    clearAllTrains() {
        this.selectedYear.selectedTrainIds = [];
    }

    selectNorthboundTrains() {
        const northBound = this.selectedYear.validTrains
            .filter(a => Number(a.trainNumber_FiveDigits.slice(-1)) % 2 === 0)
            .map(a => a.trainNumber_FiveDigits);

        const uniqueIds = new Set([...this.selectedYear.selectedTrainIds, ...northBound].sort());
        this.selectedYear.selectedTrainIds = Array.from(uniqueIds);
    }

    selectSouthboundTrains() {
        const southbound = this.selectedYear.validTrains
            .filter(a => Number(a.trainNumber_FiveDigits.slice(-1)) % 2 === 1)
            .map(a => a.trainNumber_FiveDigits);

        const uniqueIds = new Set([...this.selectedYear.selectedTrainIds, ...southbound].sort());
        this.selectedYear.selectedTrainIds = Array.from(uniqueIds);
    }

    updateSelectedTrainNumbers(event: Select2UpdateEvent<Select2UpdateValue>): void {
        this.selectedYear.selectedTrainIds = event.value as Select2UpdateValue[];
    }

    searchTrainNumbers(event: Select2SearchEvent) {
        event.filteredData(
            event.search
                // @ts-expect-error - Incorrect typing from lib
                ? event.data[0].options.filter(option => {
                    return option.label.toLowerCase().startsWith(event.search.toLowerCase());
                })
                : event.data,
        );
    }

    toggleTrafficYear(selectedTrafficYear: string) {
        const selectedTrafficYearData = this.trafficData.find(trafficYear => trafficYear.name === selectedTrafficYear);
        if (selectedTrafficYearData && selectedTrafficYearData.name !== this.selectedYear.name) {
            this.setSelectedYearData(selectedTrafficYearData)
        }
    }
}
